(ns dre.pages.annotator.views
  (:require
   ["antd" :refer [Spin Row Col]]
   [applied-science.js-interop :as j]
   [clojure.string :as str]
   [dre.coll :refer [distinct-by]]
   [dre.components.collapse :refer [collapse]]
   [dre.components.breadcrumb :refer [breadcrumb]]
   [dre.components.dropdown :refer [dropdown dropdown-list]]
   [dre.components.icon :refer [svg icon fa-icon]]
   [dre.components.icons-iconoir :refer [icoinor-icon]]
   [dre.components.inputs :refer [input-textarea]]
   [dre.components.list :refer [collapsable-list]]
   [dre.components.search-results.annotations :refer [annotate]]
   [dre.components.search-results.views.article-meta :refer [long-article-tags]]
   [dre.components.subject :as subject]
   [dre.components.tooltip :refer [tooltip-wrapper]]
   [dre.i18n.views :refer [tr]]
   [dre.i18n.events :as-alias i18n]
   [dre.modals.events :as-alias modals]
   [dre.pages.annotator.events :as events]
   [dre.page-util :refer [dangerous unescape]]
   [dre.shared.interface.names :refer [docsearch-tags]]
   [dre.utils.bem :as bem]
   [dre.util :as util]
   [re-frame.core :as rf]
   [reagent.core :as r]))

(def component-name :annotator)

(def pico-map
  {:intervention "i"
   :characteristic "c"
   :outcome "o"
   :misc "misc"})

(defn pico-label
  [pico]
  [:span
   {:class (bem/be component-name :frequency-phrase-tag)}
   [:span
    {:class [:label :pill :freetext]}
    [:span (str/capitalize (name pico))]]])

(defn ds-tag-label
  [ds-tag]
  [:span 
   {:class (bem/be component-name :frequency-phrase-tag)}
   [:span
    {:class [:label :pill :freetext]}
    [:span
     (str/capitalize
      (docsearch-tags (name ds-tag)))]]])


(defn input-box
  [loading?]
  [:div 
   {:class [(bem/be component-name :input-box)
            (bem/be component-name :panel)]}
   [collapse
    [{:label (r/as-element [:b [tr "Annotate text"]])
      :children (r/as-element [:<>
                               [:div 
                                {:class (bem/be component-name :input-box-textarea)}
                                [input-textarea {:on-change #(rf/dispatch [::events/update-input-text (-> % .-target .-value)])
                                                :value @(rf/subscribe [::events/get-input-text])}]]
                               (if loading?
                                 [:button
                                  {:class [(bem/bm :button [:brand :medium :disabled])
                                           (bem/be component-name :input-box-annotate-button)]}
                                  [tr "Annotate"]
                                  [:span 
                                   {:class (bem/be component-name :input-box-annotate-loading)}
                                   [:> Spin]]]
                                 [:button
                                  {:class [(bem/bm :button [:brand :medium])
                                           (bem/be component-name :input-box-annotate-button)]
                                   :onClick #(rf/dispatch [::events/annotate])}
                                  [tr "Annotate"]])])
      :extra (r/as-element
              [tooltip-wrapper "Configuration" [:span
                                                {:on-click (fn [e]
                                                             (.stopPropagation e)
                                                             (rf/dispatch [::modals/set-modal :annotator/config]))}
                                                [fa-icon :settings]]])}]
    :all]])


(defn frequency-table
  [results]
  (let [picos pico-map
        ds-tags docsearch-tags
        ds-tags (map (fn [[abbr label]]
                       {:id abbr
                        :label label})
                     ds-tags)
        options (map (fn [p]
                       {:id p
                        :label (str/capitalize (name p))})
                     (cons :all (keys picos)))
        options (concat options ds-tags)
        selected-option-frequency @(rf/subscribe [::events/selected-option-frequency options])

        
        filter-key (:id selected-option-frequency)
        {:keys [report]} results
        subjects report
        subjects (remove :is-suggestion? subjects)
        subjects (sort-by #(vector (- (:count %))
                                   (-> % :phrase first :label))
                          (distinct-by :phrase subjects))
        remove-misc #(update % :pico (comp (partial remove #{:misc}) set))
        subjects (case filter-key
                   :all (->> subjects
                             (map remove-misc)
                             (filter #(or (seq (:pico %))
                                          (seq (:ds-tags %)))))
                   :misc (->> subjects
                              (filter #(contains? (set (:pico %)) :misc)))
                   (->> subjects
                        (map remove-misc)
                        (filter #(or (contains? (set (:pico %)) filter-key)
                                     (contains? (set (:ds-tags %)) filter-key)))))
        prf #(str/join ", " (map unescape
                                 (util/distinct-case-insensitive %)))]
    [:div
     {:class [(bem/be component-name :frequency-table)
              (bem/be component-name :panel)]}
     [collapse
      [{:label (r/as-element [:b [tr "Frequency Table"]])
        :children (r/as-element [:div
                                 {:class (bem/be component-name :frequency-table-content)}
                                 [collapsable-list
                                  (mapv (fn [{:keys [phrase unbound pico ds-tags count]}]
                                          {:content (r/as-element [:div.frequency-table-list-item
                                                                   [:> Row
                                                                    {:gutter 16}
                                                                    [:> Col
                                                                     {:span 2}
                                                                     count]
                                                                    [:> Col
                                                                     {:span 10}
                                                                     [subject/phrase phrase nil]]
                                                                    [:> Col
                                                                     {:span 8}
                                                                     (prf unbound)]
                                                                    [:> Col
                                                                     {:span 4}
                                                                     [:span 
                                                                      {:style {:display "flex"}}
                                                                      (or (concat
                                                                          (map
                                                                           (fn [p] ^{:key p}
                                                                             [pico-label p])
                                                                           (set pico))
                                                                          (map
                                                                           (fn [p] ^{:key p}
                                                                             [ds-tag-label p])
                                                                           (set ds-tags)))
                                                                         [:misc])]]]])}) subjects)
                                  -1]])
        :extra (r/as-element [dropdown
                              {:alignment :right
                               :anchor
                               [:button
                                {:class [(bem/bm :button :light) (bem/bm :button :small)]}
                                (:label selected-option-frequency)
                                "\u00A0"
                                (icoinor-icon :nav-arrow-down)]
                               :content
                               [dropdown-list
                                (mapv (fn [v] [:span
                                               {:on-click (fn [e] 
                                                            (rf/dispatch [::events/set-selected-option-frequency v]))}
                                               (:label v)]) options)]}])}]
      :all]]))

(defn metadata
  [response]
  [:div
   {:class [(bem/be component-name :metadata)
            (bem/be component-name :panel)]}
   [collapse
    [{:label (r/as-element [:b [tr "Machine Learning Tags"]])
      :children (r/as-element [long-article-tags response])}]
    :all]])

(defn article-mount-and-decorate
  [article]
  (let [article-str (:text article)
        picos (map (fn [e]
                     (list (first (:ftspan e))
                           (last (:ftspan e))
                           {:type (get pico-map (first (:pico e)) "misc")
                            :phrase (mapv #(list (or (:sense %) "")
                                                 (str/replace (:label %) #"'" "`")
                                                 (or (:is-suggestion %) false)) (:phrase (:top-subject e)))
                            :subject-id  (:subject-id (:top-subject e))})) (get-in article [:highlights]))]
    [annotate article-str [] picos true]))

(defn annotations
  [results highlight?]
  [:div
   {:class [(bem/be component-name :highlights)
            (bem/be component-name :panel)]}
   [collapse
    [{:label (r/as-element [:b [tr "Annotations"]])
      :children (r/as-element [:div
                               {:class [:highlights
                                        (when highlight? :highlighted)]}
                               [article-mount-and-decorate results]])}]
    :all]])

;; TODO add pdf annotation option
(defn view
  []
  (let [loading? @(rf/subscribe [::events/loading?])
        response @(rf/subscribe [::events/response])
        results @(rf/subscribe [::events/results])]
    [:div
     {:class component-name}
     [:div
      {:class (bem/be component-name :nav)}
      [breadcrumb]]
     [:div
      {:class (bem/be component-name :content)}
      [input-box loading?]
      (when (and (seq results) (not loading?))
        [:<>
         [frequency-table results]
         [metadata response]
         [annotations results true]])]]))
